import PropTypes from 'prop-types';
import { css, Global } from '@emotion/react';
import styled from '@emotion/styled';
import { mediaQuery, Spacings } from '@styles/index';
import { TEST_SERIES_TAB } from './TestSeriesTabSwitcher';
import BrowseCards from './BrowseCards';
import RecommendedCard from './RecommendedCard';
import SubscriptionToggle, {
  getSubscriptionIdsForToggle,
  getSubscriptionToggleValue
} from './SubscriptionToggle';
import TestSeriesFilters, { TEST_SERIES_FILTER } from './TestSeriesFilters';
import TestSeriesListingTestCardsGrid from './TestSeriesListingTestCardsGrid';
import TestTabV2SelectedPanel from './TestTabV2SelectedPanel';
import { showFiltersForTab } from '../testTab/constants';

const hideGoalHeaderOnMobile = css`
  ${mediaQuery.uptoMobile} {
    header {
      display: none !important;
    }

    html,
    body {
      overflow: hidden;
    }
  }
`;

const PageToggle = styled.div`
  grid-column: span 18;
  width: 852px;
  max-width: 100%;
  margin-bottom: ${Spacings.SPACING_6B};

  ${mediaQuery.uptoMobile} {
    grid-column: span 10;
    width: 100%;
  }
`;

const RAIL_WIDTH = '196px';
const PINNED_OFFSET = `calc(${Spacings.SPACING_18B} + 40px)`;

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: span 18;
  width: 852px;
  max-width: 100%;

  ${({ $isPinned }) =>
    $isPinned
      ? `
    height: calc(100vh - ${PINNED_OFFSET});
    min-height: 0;
    overflow: hidden;
  `
      : ''}

  ${mediaQuery.uptoMobile} {
    grid-column: span 10;
    width: 100%;
    ${({ $isPinned }) =>
      $isPinned
        ? `
      position: fixed;
      inset: 0;
      z-index: 4;
      height: 100dvh;
      min-height: 0;
      overflow: hidden;
      background: var(--color-base-0);

      & > ${PageToggle} {
        display: none;
      }

      & > section {
        display: none;
      }
    `
        : `
      height: auto;
      overflow: visible;
    `}
  }

  ${PageToggle} {
    flex-shrink: 0;
    width: 100%;
    grid-column: unset;
    margin-bottom: ${({ $isPinned }) =>
      $isPinned ? Spacings.SPACING_4B : Spacings.SPACING_6B};
  }

  & > section {
    width: 100%;
    grid-column: unset;
    margin-bottom: ${({ $isPinned }) =>
      $isPinned ? Spacings.SPACING_4B : Spacings.SPACING_6B};
    ${({ $isPinned }) => ($isPinned ? 'flex-shrink: 0;' : '')}
  }
`;

const Stage = styled.div`
  display: flex;
  align-items: ${({ $isPinned }) => ($isPinned ? 'stretch' : 'flex-start')};
  gap: ${Spacings.SPACING_4B};
  width: 100%;
  ${({ $isPinned }) =>
    $isPinned
      ? `
    flex: 1;
    min-height: 0;
  `
      : `margin-bottom: ${Spacings.SPACING_6B};`}

  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    ${({ $isPinned }) =>
      $isPinned
        ? `
      flex: 1;
      min-height: 0;
    `
        : `
      flex: none;
      min-height: 0;
    `}
  }
`;

const RailWrap = styled.div`
  ${({ $isPinned }) =>
    $isPinned
      ? `
    flex-shrink: 0;
    width: ${RAIL_WIDTH};
  `
      : 'width: 100%;'}

  ${mediaQuery.uptoMobile} {
    width: 100%;
    ${({ $isPinned }) => ($isPinned ? 'display: none;' : '')}
  }

  & > section {
    width: 100%;
  }
`;

const TestTabV2Content = ({
  activeTestTab,
  activeCategoryTab,
  appliedFilters,
  recommended,
  onRecommendedUpsell,
  onRandomTestToggle,
  onSubscriptionToggle,
  showSubscriptionToggle,
  browseSections,
  isBrowseLoading,
  selectedBrowseSectionId,
  filterConfig,
  cards,
  isInitialLoading,
  isRefreshing,
  isError,
  hasMore,
  onLoadMore,
  isValidating,
  onCardClick,
  onBrowseSelect,
  onBrowseClose,
  onAppliedSelectionsChange,
  hasPitch
}) => {
  const selectedBrowseSection = browseSections.find(
    (section) => section.id === selectedBrowseSectionId
  );
  const isOnAllTab = activeTestTab === TEST_SERIES_TAB.ALL;
  const isBrowseSelected = isOnAllTab && Boolean(selectedBrowseSectionId);
  const cardsGrid = (
    <TestSeriesListingTestCardsGrid
      cards={cards}
      isInitialLoading={isInitialLoading}
      isRefreshing={isRefreshing}
      isError={isError}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      isValidating={isValidating}
      onCardClick={onCardClick}
      isFlush={isBrowseSelected}
    />
  );
  const renderSubscriptionToggle = () =>
    isOnAllTab && showSubscriptionToggle ? (
      <PageToggle>
        <SubscriptionToggle
          value={getSubscriptionToggleValue(
            appliedFilters[TEST_SERIES_FILTER.SUBSCRIPTION]
          )}
          onChange={(toggleValue) => {
            onSubscriptionToggle?.(toggleValue);
            onAppliedSelectionsChange({
              ...appliedFilters,
              [TEST_SERIES_FILTER.SUBSCRIPTION]:
                getSubscriptionIdsForToggle(toggleValue)
            });
          }}
        />
      </PageToggle>
    ) : null;

  const renderFilters = () =>
    showFiltersForTab(activeTestTab, activeCategoryTab) ? (
      <TestSeriesFilters
        filterConfig={filterConfig}
        appliedSelections={appliedFilters}
        onAppliedSelectionsChange={onAppliedSelectionsChange}
      />
    ) : null;
  const recommendedCard =
    isOnAllTab && recommended ? (
      <RecommendedCard
        data={recommended}
        isCompact={isBrowseSelected}
        onStart={(option) =>
          onCardClick?.({
            variant: 'quiz',
            id: option.quizUid,
            title: option.label,
            href: option.href,
            source: 'recommended',
            duration: option.label,
            durationBucket: option.durationBucket
          })
        }
        onUpsell={onRecommendedUpsell}
        onToggle={onRandomTestToggle}
      />
    ) : null;
  const browseRail = (
    <BrowseCards
      sections={browseSections}
      isLoading={isBrowseLoading}
      isRail={isBrowseSelected}
      activeSectionId={selectedBrowseSectionId}
      onSelect={onBrowseSelect}
    />
  );
  return (
    <>
      {isBrowseSelected ? <Global styles={hideGoalHeaderOnMobile} /> : null}
      <Layout $isPinned={isBrowseSelected}>
        {renderSubscriptionToggle()}
        {recommendedCard}
        {isOnAllTab ? (
          <Stage $isPinned={isBrowseSelected}>
            <RailWrap $isPinned={isBrowseSelected}>{browseRail}</RailWrap>
            {isBrowseSelected ? (
              <TestTabV2SelectedPanel
                section={selectedBrowseSection}
                toggle={renderSubscriptionToggle()}
                filters={renderFilters()}
                hasPitch={hasPitch}
                onClose={onBrowseClose}
              >
                {cardsGrid}
              </TestTabV2SelectedPanel>
            ) : null}
          </Stage>
        ) : null}
      </Layout>
      {activeTestTab === TEST_SERIES_TAB.MY ? cardsGrid : null}
    </>
  );
};

export default TestTabV2Content;

TestTabV2Content.propTypes = {
  activeTestTab: PropTypes.string.isRequired,
  activeCategoryTab: PropTypes.string.isRequired,
  appliedFilters: PropTypes.object.isRequired,
  recommended: PropTypes.object,
  onRecommendedUpsell: PropTypes.func,
  onRandomTestToggle: PropTypes.func,
  onSubscriptionToggle: PropTypes.func,
  showSubscriptionToggle: PropTypes.bool,
  browseSections: PropTypes.arrayOf(PropTypes.object).isRequired,
  isBrowseLoading: PropTypes.bool,
  selectedBrowseSectionId: PropTypes.string,
  filterConfig: PropTypes.arrayOf(PropTypes.object),
  cards: PropTypes.array,
  isInitialLoading: PropTypes.bool,
  isRefreshing: PropTypes.bool,
  isError: PropTypes.bool,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  isValidating: PropTypes.bool,
  onCardClick: PropTypes.func,
  onBrowseSelect: PropTypes.func.isRequired,
  onBrowseClose: PropTypes.func.isRequired,
  onAppliedSelectionsChange: PropTypes.func.isRequired,
  hasPitch: PropTypes.bool
};

TestTabV2Content.defaultProps = {
  recommended: null,
  onRecommendedUpsell: () => {},
  onRandomTestToggle: () => {},
  onSubscriptionToggle: () => {},
  showSubscriptionToggle: true,
  isBrowseLoading: false,
  selectedBrowseSectionId: null,
  filterConfig: [],
  cards: [],
  isInitialLoading: false,
  isRefreshing: false,
  isError: false,
  hasMore: false,
  onLoadMore: undefined,
  isValidating: false,
  onCardClick: undefined,
  hasPitch: false
};
